import axios, { AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import router from '@/router/index';
import { map, flatMap } from 'lodash';
import Vue from 'vue';
import store from '@/store';

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

const apiInstance = axios.create({
  baseURL: `${process.env.VUE_APP_API_BASE_ADDRESS}/ecare/v1/`
});

const pending = new Map<string, CancelTokenSource>();

function addPending(config: AxiosRequestConfig): boolean {
  const request: string = constructRequestObject(config);

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  config.cancelToken = source.token;

  if (!pending.has(request)) {
    // If the current request does not exist in pending, add it
    pending.set(request, source);
    return true;
  }
  return false;
}

function removePending(config: AxiosRequestConfig) {
  const request: string = constructResponseRequestObject(config);

  if (pending.has(request)) {
    pending.delete(request);
  }
}

function constructRequestObject(config: AxiosRequestConfig) {
  let request: string;
  if (config.data instanceof FormData) {
    const requestArray = [config.method, config.url, JSON.stringify(config.params)];

    let data = '';
    for (const pair of config.data.entries()) {
      data = data.concat(`${JSON.stringify(pair[0])}:${JSON.stringify(pair[1])}`);
    }

    requestArray.push(data);
    request = requestArray.join('&');
  } else {
    request = [config.method, config.url, JSON.stringify(config.params), JSON.stringify(config.data)].join('&');
  }
  return request;
}

function constructResponseRequestObject(config: AxiosRequestConfig) {
  let request: string;

  if (config.data instanceof FormData) {
    const requestArray = [config.method, config.url, JSON.stringify(config.params)];

    let data = '';
    for (const pair of config.data.entries()) {
      data = data.concat(`${JSON.stringify(pair[0])}:${JSON.stringify(pair[1])}`);
    }

    requestArray.push(data);
    request = requestArray.join('&');
  } else {
    request = [config.method, config.url, JSON.stringify(config.params), config.data].join('&');
  }
  return request;
}

function clearPending() {
  for (const [url, source] of pending) {
    source.cancel();
  }
  pending.clear();
}

apiInstance.interceptors.request.use(
  (config: any) => {
    const isNotDuplicate = addPending(config);

    if (isNotDuplicate) {
      return config;
    } else {
      return Promise.reject('duplicate');
    }
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

apiInstance.interceptors.response.use(
  (response: any) => {
    removePending(response.config); // Remove this request at the end of the request
    return response;
  },
  (error: { response: { config: any } }) => {
    if (error.response) {
      removePending(error.response.config); // Remove this request at the end of the request
    }
    return Promise.reject(error);
  }
);

apiInstance.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('access-token');

    if (token != null) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

apiInstance.interceptors.response.use(
  (response: any) => {
    if (Object.prototype.hasOwnProperty.call(response.data, 'message') && Object.prototype.hasOwnProperty.call(response.data, 'data')) {
      if (response.message) {
        const allMsg = map(response.message.messages, 'message');
        Vue.prototype.$showAlert(allMsg);
      }

      return response.data;
    } else {
      return response;
    }
  },
  (error: any) => {
    Vue.prototype.$hideLoading();
    Vue.prototype.$hideNavigationLoading();

    if (error === 'duplicate') {
      return Promise.reject('duplicate request detected');
    }

    if (error.response) {
      let messages = [];

      messages = typeof error.response.data === 'string' ? [error.response.data] : flatMap(error.response.data, (item: any) => Object.values(item));

      if (error.response.status === 403 && router.currentRoute.name !== 'AccessDenied') {
        router.push({ name: 'AccessDenied' });
      } else if (error.response.status === 400) {
        Vue.prototype.$showAlert(messages, 'error');
      } else if (error.response.status === 500) {
        Vue.prototype.$showAlert(messages, 'error');
      } else if (error.response.status === 401) {
        store.dispatch('logout').then(() => router.push({ name: 'Login' }));
      } else if (error.response.status === 409) {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else if (error.response.status === 419) {
        Vue.prototype.$showNotification('error', 'رمز عبور شما منقضی شده است.');
        if (router.currentRoute.name !== 'ChangePassword') {
          router.push({ name: 'ChangePassword' });
        }
      } else {
        Vue.prototype.$showAlert('خطا در پردازش درخواست.', 'error');
      }

      return Promise.reject(error);
    }
  }
);

export default {
  api: apiInstance,
  cancelPendingRequests: clearPending
};
